import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Breadcrumbs as BreadcrumbsMUI,
  styled,
  Typography,
} from "@mui/material";
import { type FileRouteTypes, Link } from "@tanstack/react-router";

export interface BreadcrumbItem {
  section: string;
  route: FileRouteTypes["to"] | "";
}

export interface BreadcrumbsProps {
  breadcrumbs: BreadcrumbItem[];
  disabled?: boolean;
}

const StyledLink = styled(Link)(({ theme, disabled }) => ({
  display: "flex",
  alignItems: "center",
  cursor: disabled ? "not-allowed" : "pointer",
  textDecoration: "none",
  pointerEvents: disabled ? "none" : "auto",
  "&:hover": {
    textDecoration: disabled ? "none" : "underline",
    color: theme.palette.text.primary,
  },
  "&:active": {
    color: theme.palette.text.primary,
  },
}));

export function Breadcrumbs({
  breadcrumbs,
  disabled = false,
}: BreadcrumbsProps) {
  return (
    <BreadcrumbsMUI
      aria-label="Breadcrumb"
      separator={
        <ChevronRightIcon
          fontSize="small"
          sx={(theme) => ({
            width: 36,
            height: 20,
            color: disabled
              ? theme.palette.action.disabled
              : theme.palette.action.active,
          })}
        />
      }
      sx={{
        "& .MuiBreadcrumbs-separator": {
          margin: "0",
        },
      }}
    >
      {breadcrumbs.map(({ section, route }, index) => {
        const isLastItem = index === breadcrumbs.length - 1;
        const isClickable = route !== "" && !disabled;
        // Render non-clickable breadcrumbs as Typography (no hover effect)
        if (!isClickable) {
          return (
            <Typography
              key={section}
              aria-current={isLastItem ? "page" : undefined}
              variant="caption"
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                color:
                  isLastItem && disabled
                    ? theme.palette.primary.light
                    : isLastItem
                      ? theme.palette.primary.dark
                      : disabled
                        ? theme.palette.text.disabled
                        : theme.palette.text.primary,
              })}
            >
              {section}
            </Typography>
          );
        }

        // Render clickable breadcrumbs as Link
        return (
          <StyledLink key={section} to={route} disabled={disabled}>
            <Typography
              variant="caption"
              sx={(theme) => ({
                color: isLastItem
                  ? theme.palette.primary.dark
                  : theme.palette.text.primary,
              })}
            >
              {section}
            </Typography>
          </StyledLink>
        );
      })}
    </BreadcrumbsMUI>
  );
}
